import { basilWriteRefusal, BasilWriteSubject } from './canBasilWrite';
import { findDuplicateAddresses } from './chunk';
import { containsProhibitedName, spacedKey } from './prohibitedNames';
import { assertEditContextWireIds, BasilSendRefusal, isWireId } from './sendGuard';
import {
  ApplyRefusal,
  ApplyStatusEntry,
  BasilPlan,
  EngineId,
  RequestStatus,
} from './writeTypes';

/**
 * The request fields the preflight reads. The planner rebuilds `plan.request` from named fields
 * (`plan.ts:433-524`), so this view is a read of that union, never a widening of it.
 */
interface PlannedRequestView {
  op?: string;
  branchId?: EngineId;
  stepId?: EngineId;
  answerId?: EngineId;
  text?: string;
}

/**
 * W1 — the preflight, in the order spec section 5.1 gives, before any body is built.
 *
 * W1.1 the gate (`canBasilWrite`, environment-aware per amendment A1) — `canBasilWrite.ts`
 * W1.2 the live denylist — same file, named separately
 * W1.3 the verifier handshake — the caller passes its result in (E17)
 * W1.4 the plan is wholly allow-listed — here
 * W1.5 nothing that needs the owner or an unfired canary — here
 * W1.6 every id about to be sent is an integer — here
 *
 * Fail closed at every step: a step that cannot prove its condition refuses; it never degrades to
 * "probably fine".
 */

/** The only ops that may be SENT today. `append_step` is planned, drafted and refused (W1.5). */
export const SENDABLE_OPS: readonly string[] = ['set_step_text', 'set_answer_text'];

/** `plan.ts:99-103` — the planner's whole allowed request surface, per op. */
export const ALLOWED_REQUEST_KEYS: Record<string, readonly string[]> = {
  set_step_text: ['op', 'branchId', 'stepId', 'text', 'testDraft'],
  set_answer_text: ['op', 'branchId', 'stepId', 'answerId', 'text', 'testDraft'],
};

/**
 * Book-scoped resources Basil never mutates or deletes — the owner's word, always (AD1:71-74,
 * law 8). A request key naming one of them needs the owner, not a refusal by shape.
 */
export const BOOK_SCOPED_KEY_PATTERN = /variable|location|background|character|expression|icon|achievement|memory/i;

/** `plan.ts:93` — publish-like in any spelling. */
// eslint-disable-next-line max-len
const PUBLISH_LIKE = /publish|go[ _-]?live|update[ _-]?live|update[ _-]?in[ _-]?app|in[ _-]?app[ _-]?update|release|deploy|prepublish|submit|restore[ _-]?active|push|save[ _-]?as[ _-]?(?:template|prompt)|share|translate|gamepass/i;

/** `plan.ts:94` — delete, insert, move, reorder, by name (AD1:50). */
const STRUCTURAL_FORBIDDEN = /delete|remove|reorder|move|insert/i;

/**
 * The request keys whose value is a WRITER'S SENTENCE rather than a name of anything.
 *
 * This distinction is the whole of the fix. `PUBLISH_LIKE` and `STRUCTURAL_FORBIDDEN` are op-name
 * and route-name expressions — `plan.ts` applies them to `request.op` and to nothing else
 * (`plan.ts:272-274`), and `sendGuard` applies them to the path. Applied to prose they refuse the
 * writing: "I move closer.", "Delete the letter.", "Insert the key and publish nothing." are all
 * ordinary narrative, and a surface that cannot send them is not a writing surface.
 *
 * A writer's sentence is therefore scanned for one thing only, law 9's AI/model/vendor names —
 * which is exactly what the planner scans a string value for (`plan.ts:182-201`). Keys, op names
 * and every other string value keep the full identifier scan.
 */
const WRITER_TEXT_KEYS: readonly string[] = ['text'];

export interface PreflightArgs {
  subject: BasilWriteSubject;
  plans: readonly BasilPlan[];
  /** W1.3: `verifyClient.handshake()` returned an artifact identity and the client is available. */
  verifierAvailable: boolean;
}

export interface PreflightResult {
  ok: boolean;
  refusal: ApplyRefusal | null;
  message: string | null;
  /** keyed by planId, exactly as `ApplyState.statuses` (section 8.4). */
  statuses: Record<string, ApplyStatusEntry>;
  /** the plans that may be sent, in order; empty whenever `ok` is false. */
  sendable: BasilPlan[];
}

/**
 * One preflight status. Every one of them is `not-sent`, by construction: the preflight runs before
 * any body is built, so no request it describes can have left the browser (P4).
 */
function status(state: RequestStatus, message?: string): ApplyStatusEntry {
  const entry: ApplyStatusEntry = { status: state, chunkIndex: 0, delivery: 'not-sent' };

  return message === undefined ? entry : { ...entry, message };
}

function refusedPlan(reason: ApplyRefusal, message: string, statuses: Record<string, ApplyStatusEntry>): PreflightResult {
  return {
    ok: false, refusal: reason, message, statuses, sendable: [],
  };
}

/** Every string in a request, sorted by what it IS: a key, an op name, an identifier or prose. */
export interface RequestStrings {
  /** every key name, at any depth. */
  keys: string[];
  /** every value of an `op` key. */
  ops: string[];
  /** every other string value: ids, enums, scope markers — things that name something. */
  identifiers: string[];
  /** every value of a writer-text key: sentences the writer typed. */
  texts: string[];
}

export function collectRequestStrings(value: unknown, key: string | null, into: RequestStrings): void {
  if (typeof value === 'string') {
    if (key === 'op') {
      into.ops.push(value);
    } else if (key !== null && WRITER_TEXT_KEYS.indexOf(key) !== -1) {
      into.texts.push(value);
    } else {
      into.identifiers.push(value);
    }

    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectRequestStrings(item, key, into));

    return;
  }

  if (value && typeof value === 'object') {
    Object.keys(value as Record<string, unknown>).forEach((child) => {
      into.keys.push(child);
      collectRequestStrings((value as Record<string, unknown>)[child], child, into);
    });
  }
}

/** W1.4 and W1.5, for one plan. Returns the named refusal or null. */
export function refusalForPlan(plan: BasilPlan): { refusal: ApplyRefusal; message: string } | null {
  if (plan.writePath === 'whole-branch') {
    return {
      refusal: 'needs-canary',
      message: (plan.wholeBranchDraft && plan.wholeBranchDraft.reason) || "Adding scenes isn't something Basil can do yet.",
    };
  }

  if (plan.needsCanary) {
    return { refusal: 'needs-canary', message: "Basil can't make this kind of change yet." };
  }

  const request: PlannedRequestView = (plan.request || {}) as PlannedRequestView;
  const op = typeof request.op === 'string' ? request.op : '';

  // The op-name refusals run FIRST and on the op itself, exactly as the planner does
  // (`plan.ts:272-274`), so a publish-like or structural operation is refused by its own name and
  // not merely as "not sendable".
  if (PUBLISH_LIKE.test(op)) {
    return { refusal: 'plan-refused', message: 'Basil never makes an episode live, so this change is paused.' };
  }

  if (STRUCTURAL_FORBIDDEN.test(op)) {
    return { refusal: 'plan-refused', message: "Basil can't add, remove or move parts of the script yet." };
  }

  if (SENDABLE_OPS.indexOf(op) === -1) {
    return { refusal: 'plan-refused', message: "Basil can't make this kind of change." };
  }

  const words: RequestStrings = {
    keys: [], ops: [], identifiers: [], texts: [],
  };

  collectRequestStrings(request, null, words);

  const named = words.keys.concat(words.ops, words.identifiers);
  const publishLike = named.find((word) => PUBLISH_LIKE.test(word));

  if (publishLike) {
    return { refusal: 'plan-refused', message: 'Basil never makes an episode live, so this change is paused.' };
  }

  const structural = named.find((word) => STRUCTURAL_FORBIDDEN.test(word));

  if (structural) {
    return { refusal: 'plan-refused', message: "Basil can't add, remove or move parts of the script yet." };
  }

  // Law 9, on keys and on every string value including the writer's own text — the one scan a
  // sentence is subject to (`plan.ts:182-201`). The refusal names the field, never the word.
  const prohibitedKey = words.keys.find((key) => containsProhibitedName(spacedKey(key)));

  if (prohibitedKey) {
    return { refusal: 'plan-refused', message: "This change uses a word Basil can't put in the episode, so it's paused." };
  }

  if (words.identifiers.concat(words.texts).some(containsProhibitedName)) {
    return { refusal: 'plan-refused', message: "This change uses a word Basil can't put in the episode, so it's paused." };
  }

  const allowed = ALLOWED_REQUEST_KEYS[op] || [];
  const unknownKey = Object.keys(request).find((key) => allowed.indexOf(key) === -1);

  if (unknownKey) {
    if (BOOK_SCOPED_KEY_PATTERN.test(unknownKey)) {
      return { refusal: 'needs-owner', message: "This change touches the whole book, so it needs the owner's approval." };
    }

    return { refusal: 'plan-refused', message: "This change includes something Basil doesn't recognise." };
  }

  // W1.6 — the wire is stricter than the toolchain: a minted or string id is a 422.
  if (!isWireId(request.branchId)) {
    return { refusal: 'plan-refused', message: "Basil can't tell which scene this change belongs to." };
  }

  if (request.stepId !== undefined && !isWireId(request.stepId)) {
    return { refusal: 'plan-refused', message: "Basil can't tell which line this change belongs to." };
  }

  if (request.answerId !== undefined && !isWireId(request.answerId)) {
    return { refusal: 'plan-refused', message: "Basil can't tell which answer this change belongs to." };
  }

  if (!plan.editContextPatch || plan.editContextPatch.nodes.length === 0) {
    return { refusal: 'plan-refused', message: "There's nothing in this change to send." };
  }

  try {
    assertEditContextWireIds(plan.editContextPatch);
  } catch (error) {
    const refusal = error instanceof BasilSendRefusal ? error.message : String(error);

    return { refusal: 'plan-refused', message: refusal };
  }

  return null;
}

/** The whole preflight. Nothing is built, and no request is issued, until this returns `ok`. */
export function preflight(args: PreflightArgs): PreflightResult {
  const statuses: Record<string, ApplyStatusEntry> = {};
  const gate = basilWriteRefusal(args.subject);

  if (gate) {
    return {
      ok: false, refusal: gate, message: "Basil can't make changes to this episode right now, so nothing was sent.", statuses, sendable: [],
    };
  }

  if (!args.verifierAvailable) {
    return {
      ok: false,
      refusal: 'verifier-unavailable',
      message: "Basil's double-check isn't answering, so nothing was sent.",
      statuses,
      sendable: [],
    };
  }

  if (args.plans.length === 0) {
    return {
      ok: false, refusal: 'nothing-to-apply', message: "There's nothing to apply.", statuses, sendable: [],
    };
  }

  const refusals: Array<{ refusal: ApplyRefusal; message: string }> = [];

  for (let index = 0; index < args.plans.length; index += 1) {
    const plan = args.plans[index];
    const refusal = refusalForPlan(plan);

    if (!refusal) {
      statuses[plan.planId] = status('planned');
    } else {
      refusals.push(refusal);

      if (refusal.refusal === 'needs-canary') {
        statuses[plan.planId] = status('needs-canary', refusal.message);
      } else if (refusal.refusal === 'needs-owner') {
        statuses[plan.planId] = status('needs-owner', refusal.message);
      } else {
        statuses[plan.planId] = status('refused', refusal.message);
      }
    }
  }

  if (refusals.length > 0) {
    return refusedPlan(refusals[0].refusal, refusals[0].message, statuses);
  }

  const duplicates = findDuplicateAddresses(args.plans);

  if (duplicates.length > 0) {
    args.plans.forEach((plan) => {
      statuses[plan.planId] = status('refused', 'Two of your changes touch the same place.');
    });

    return refusedPlan('plan-refused', 'Two of your changes touch the same place, so Basil paused them all.', statuses);
  }

  return {
    ok: true, refusal: null, message: null, statuses, sendable: args.plans.slice(),
  };
}
